import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ShieldCheck } from 'lucide-react';

export const ConsumerConsentPage = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="border-b border-slate-200 bg-white">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-slate-800 hover:text-slate-600 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            <span className="font-semibold text-lg">Formlio</span>
          </Link>
          <div className="flex items-center gap-2 text-indigo-600">
            <ShieldCheck className="w-5 h-5" />
            <span className="font-medium text-sm">Formlio Sign</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-6 py-16" data-testid="consumer-consent-page">
        <h1 className="text-3xl font-bold text-indigo-700 text-center mb-10" data-testid="consent-title">
          Consumer Consent Disclosure
        </h1>

        <div className="prose prose-slate max-w-none space-y-6 text-slate-700 leading-relaxed">
          <p className="text-lg font-medium text-slate-800">
            Consumer Disclosure Regarding Signing Documents Electronically, Conducting Business
            Electronically, and Receiving Electronic Documents, Notices and Disclosures
          </p>

          <p>
            By utilizing the electronic signature product or service that contained a link to this
            Consumer Consent Disclosure, you are agreeing that you have reviewed the following
            information, and you consent to utilizing electronic signatures in lieu of using
            handwritten or non-digital signatures or paper, to conduct business using electronic
            communications, and to receive notices, signatures, and disclosures electronically. You
            are not required by Formlio or by law to consent to any of these things.
          </p>

          <p>
            If you have consented to receive future electronic signature invitations or other
            notices, signatures, or disclosures electronically from Formlio, and you want to
            withdraw that consent, you may email us at{' '}
            <a href="mailto:support@formlio.app" className="text-indigo-600 hover:text-indigo-800 font-medium">
              support@formlio.app
            </a>
            . If you do not agree to utilize electronic signatures in lieu of using handwritten or
            non-digital signatures or paper with respect to a party who is using Formlio Sign to
            send you documents to electronically sign, please email that party regarding that matter.
          </p>

          <h2 className="text-xl font-semibold text-slate-900 mt-10">Your Rights</h2>

          <p>
            You have the right to receive documents in paper form. If you wish to receive a paper
            copy of any document provided electronically, you may contact the sender directly or
            email us at{' '}
            <a href="mailto:support@formlio.app" className="text-indigo-600 hover:text-indigo-800 font-medium">
              support@formlio.app
            </a>
            .
          </p>

          <h2 className="text-xl font-semibold text-slate-900 mt-10">Electronic Signature Validity</h2>

          <p>
            Your electronic signature on documents executed through Formlio Sign will have the same
            legal effect, validity, and enforceability as a manually executed signature to the
            fullest extent permitted by applicable law, including but not limited to the Electronic
            Signatures in Global and National Commerce Act (E-SIGN Act), the Uniform Electronic
            Transactions Act (UETA), and similar laws in other jurisdictions.
          </p>

          <h2 className="text-xl font-semibold text-slate-900 mt-10">System Requirements</h2>

          <p>
            To access and use electronic signatures and receive electronic documents, you will need:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>A modern web browser (Chrome, Firefox, Safari, or Edge)</li>
            <li>An internet connection</li>
            <li>A device capable of capturing your signature (mouse, touchscreen, or stylus)</li>
          </ul>

          <h2 className="text-xl font-semibold text-slate-900 mt-10">Contact</h2>

          <p>
            For questions about this disclosure or electronic signatures through Formlio Sign, please
            contact us at{' '}
            <a href="mailto:support@formlio.app" className="text-indigo-600 hover:text-indigo-800 font-medium">
              support@formlio.app
            </a>
            .
          </p>
        </div>

        {/* Footer */}
        <div className="mt-16 pt-8 border-t border-slate-200 text-center">
          <p className="text-sm text-indigo-600 font-medium">Powered by Formlio Sign</p>
          <p className="text-xs text-slate-400 mt-2">Last updated: March 2026</p>
        </div>
      </div>
    </div>
  );
};

export default ConsumerConsentPage;
